import React, { createContext, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IUser, ITokenResponse } from 'utils/models';
import storage from 'utils/storage';
import { actions } from 'store/actions';
import { toast } from 'components/Toast';
import { useLogin, useSignup } from 'hooks/useAuthApi';
import { useGetMe } from 'hooks/useGetMe';

interface IContext {
  user: IUser | null;
  isLoggedIn: boolean;
  isOnBoarding: boolean;
  isLoading: boolean;
  token: string | null;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<ITokenResponse | void>;
  logout: () => void;
  signup: (
    email: string,
    password: string,
    first_name: string,
    last_name: string
  ) => Promise<ITokenResponse | void>;
  getMe: () => Promise<void>;
  logMe: () => void;
}

const AuthContext = createContext<IContext | undefined>({
  user: null,
  isLoggedIn: false,
  isOnBoarding: false,
  isLoading: true,
  token: null,
  isAdmin: false,
  login: async () => {},
  logout: () => {},
  signup: async () => {},
  getMe: async () => {},
  logMe: () => {}
});

const toBool = (v: any): boolean | undefined => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === 'boolean') return v;
  const s = String(v).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'yes') return true;
  if (s === 'false' || s === '0' || s === 'no') return false;
  return undefined;
};

const coalesce = <T,>(...vals: (T | undefined)[]): T | undefined =>
  vals.find((v) => v !== undefined);

/**
 * Normalize raw user data from API response
 * Feature flags are resolved from the actual user/doctor_type objects
 * No fallbacks to cached snapshots - always trust the API response
 */
const normalizeUser = (raw: any): IUser => {
  if (!raw) return raw as IUser;

  const dtRaw = raw.doctor_type ?? raw.doctorType ?? {};
  const features = raw.features ?? {};

  return {
    // Basic user fields
    id: raw.id,
    email: raw.email,
    timezone: raw.timezone,
    contactNo: raw.contact_no ?? raw.contactNo,
    phone_verification: toBool(raw.phone_verification),
    email_verification: toBool(raw.email_verification),

    // Doctor-specific fields
    doctorTypeId: raw.doctor_type_id ?? raw.doctorTypeId,
    profilePic: raw.profile_pic ?? raw.profilePic,
    firstName: raw.first_name ?? raw.firstName,
    lastName: raw.last_name ?? raw.lastName,
    status: raw.status,
    honorific: raw.honorific,
    providerNo: raw.provider_no ?? raw.providerNo,
    prescriberNo: raw.prescriber_no ?? raw.prescriberNo,
    registrationNo: raw.registration_no ?? raw.registrationNo,
    address: raw.street ?? raw.address,
    verified: toBool(raw.verified),
    doctor_id: raw.id,
    provider_no: raw.provider_no,
    prescriber_no: raw.prescriber_no,
    registration_no: raw.registration_no,
    description: raw.description,
    onboarding_step: raw.onboarding_step,

    // Patient fields (may be null for doctors)
    patient_id: raw.patient_id ?? raw.id,
    first_name: raw.first_name,
    middle_name: raw.middle_name,
    last_name: raw.last_name,
    pronoun: raw.pronoun,
    gender: raw.gender,
    date_of_birth: raw.date_of_birth ? new Date(raw.date_of_birth) : null,
    street: raw.street,
    city: raw.city,
    state: raw.state,
    postcode: raw.postcode,
    country: raw.country,
    medicare_no: raw.medicare_no,
    profile_pic: raw.profile_pic,
    is_australian_resident: raw.is_australian_resident,
    is_over_18: raw.is_over_18,
    tried_conventional_treatment: raw.tried_conventional_treatment,
    medical_symptoms: raw.medical_symptoms,
    medical_conditions: raw.medical_conditions,
    conditions: raw.conditions,
    other_conditions: raw.other_conditions,
    how_long_suffered: raw.how_long_suffered,
    using_any_medicine: raw.using_any_medicine,
    have_any_allergy: raw.have_any_allergy,
    allergy_details: raw.allergy_details,
    relevant_notes: raw.relevant_notes,
    about_polln: raw.about_polln,
    currently_being_treated: raw.currently_being_treated,
    current_prescription: raw.current_prescription,
    taking_any_medication: raw.taking_any_medication,
    taking_any_medication_list: raw.taking_any_medication_list,
    achieve_polln: raw.achieve_polln,
    addtl_info: raw.addtl_info,
    patient_notes: raw.patient_notes,
    careteam_notes: raw.careteam_notes,
    careteam_notes_last_edit_at: raw.careteam_notes_last_edit_at
      ? new Date(raw.careteam_notes_last_edit_at)
      : null,
    careteam_notes_last_edit_by: raw.careteam_notes_last_edit_by,
    weight: raw.weight,
    height: raw.height,
    health_history: raw.health_history ?? {},
    ihi_no: raw.ihi_no,
    ihi_record_status: raw.ihi_record_status,
    ihi_status: raw.ihi_status,
    ihi_timestamp: raw.ihi_timestamp,
    dva_no: raw.dva_no,
    pinned_alert: raw.pinned_alert,
    contact_no: raw.contact_no,

    profile:
      raw.profile ??
      {
        // Map profile fields if they exist at the root level or construct empty
      },

    // Feature flags - resolved from raw user or doctor_type
    // No snapshot fallback - trust the API response
    has_prescriptions: toBool(raw?.has_prescriptions) ?? toBool(dtRaw?.has_prescriptions) ?? false,
    has_supplements: toBool(raw?.has_supplements) ?? toBool(dtRaw?.has_supplements) ?? false,
    has_referrals: toBool(raw?.has_referrals) ?? toBool(dtRaw?.has_referrals) ?? false,
    has_lifestyle: toBool(raw?.has_lifestyle) ?? toBool(dtRaw?.has_lifestyle) ?? false,
    has_mental: toBool(raw?.has_mental) ?? toBool(dtRaw?.has_mental) ?? false,
    has_naturopathy: toBool(raw?.has_naturopathy) ?? toBool(dtRaw?.has_naturopathy) ?? false,
    has_nutrition: toBool(raw?.has_nutrition) ?? toBool(dtRaw?.has_nutrition) ?? false,
    has_sexual: toBool(raw?.has_sexual) ?? toBool(dtRaw?.has_sexual) ?? false,

    // Doctor type with enhanced mapping
    doctor_type: {
      ...dtRaw,
      has_prescriptions: toBool(dtRaw?.has_prescriptions) ?? false,
      has_supplements: toBool(dtRaw?.has_supplements) ?? false,
      has_referrals: toBool(dtRaw?.has_referrals) ?? false,
      has_lifestyle: toBool(dtRaw?.has_lifestyle) ?? false,
      has_mental: toBool(dtRaw?.has_mental) ?? false,
      has_naturopathy: toBool(dtRaw?.has_naturopathy) ?? false,
      has_nutrition: toBool(dtRaw?.has_nutrition) ?? false,
      has_sexual: toBool(dtRaw?.has_sexual) ?? false
    }
  } as IUser;
};

/**
 * Merge previous user state with new normalized data
 * Prefers defined values from new data, falls back to previous only if new is undefined
 */
const mergePreferDefined = <T extends Record<string, any>>(
  prev: T | null,
  next: T | null
): T | null => {
  if (!prev) return next ? ({ ...next } as T) : prev;
  if (!next) return { ...prev } as T;

  const out: any = { ...prev, ...next };

  // Handle all feature flags at user level - prefer next, fall back to prev
  if (next.has_prescriptions === undefined) out.has_prescriptions = prev.has_prescriptions;
  if (next.has_supplements === undefined) out.has_supplements = prev.has_supplements;
  if (next.has_referrals === undefined) out.has_referrals = prev.has_referrals;
  if (next.has_lifestyle === undefined) out.has_lifestyle = prev.has_lifestyle;
  if (next.has_mental === undefined) out.has_mental = prev.has_mental;
  if (next.has_naturopathy === undefined) out.has_naturopathy = prev.has_naturopathy;
  if (next.has_nutrition === undefined) out.has_nutrition = prev.has_nutrition;
  if (next.has_sexual === undefined) out.has_sexual = prev.has_sexual;

  const pdt = prev.doctor_type ?? {};
  const ndt = next.doctor_type ?? {};
  const mergedDt: any = { ...pdt, ...ndt };

  // Handle all feature flags at doctor_type level - prefer next, fall back to prev
  if (ndt.has_prescriptions === undefined) mergedDt.has_prescriptions = pdt.has_prescriptions;
  if (ndt.has_supplements === undefined) mergedDt.has_supplements = pdt.has_supplements;
  if (ndt.has_referrals === undefined) mergedDt.has_referrals = pdt.has_referrals;
  if (ndt.has_lifestyle === undefined) mergedDt.has_lifestyle = pdt.has_lifestyle;
  if (ndt.has_mental === undefined) mergedDt.has_mental = pdt.has_mental;
  if (ndt.has_naturopathy === undefined) mergedDt.has_naturopathy = pdt.has_naturopathy;
  if (ndt.has_nutrition === undefined) mergedDt.has_nutrition = pdt.has_nutrition;
  if (ndt.has_sexual === undefined) mergedDt.has_sexual = pdt.has_sexual;

  out.doctor_type = mergedDt;

  return out as T;
};

/* --------------------------------- provider --------------------------------- */

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<IUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tokenRefresh, setTokenRefresh] = useState<string | null>(null);
  const [expiry, setExpire] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isOnBoarding, setIsOnBoarding] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  const { refetch: doctorMe } = useGetMe();

  const inflight = useRef<AbortController | null>(null);
  const dispatch = useDispatch();

  const reduxUser = useSelector((s: any) => s?.user?.user ?? null);

  const loginMutation = useLogin();
  const signupMutation = useSignup();

  const handleAuth = useCallback(
    (
      meToken: string | null,
      meRaw: IUser,
      loginFlag: boolean,
      status: boolean,
      meTokenExpiration: string | null,
      meTokenRefresh: string | null,
      meAdmin: boolean
    ) => {
      if (!meToken || !meTokenExpiration || !meTokenRefresh) return;

      storage.setItem('token', meToken);
      storage.setItem('token-refresh', meTokenRefresh);
      storage.setItem('expiry', meTokenExpiration);

      setToken(meToken);
      setTokenRefresh(meTokenRefresh);
      setExpire(meTokenExpiration);

      const normalized = normalizeUser(meRaw);

      setUser((prev) => {
        const base = reduxUser ?? prev;
        const merged = mergePreferDefined(base, normalized) as IUser;

        dispatch(actions.userMe(merged));
        setIsLoggedIn(loginFlag);
        setIsAdmin(meAdmin);
        setIsOnBoarding(status);
        return merged;
      });

      dispatch(actions.userOnBoard(status));
      dispatch(actions.userLogin());
    },
    [dispatch, reduxUser]
  );

  const logout = useCallback(() => {
    inflight.current?.abort();
    storage.removeItem('token');
    storage.removeItem('expiry');
    storage.removeItem('token-refresh');

    dispatch(actions.userLogout());

    setUser(null);
    setIsLoggedIn(false);
    setIsOnBoarding(false);
    setToken(null);
    setExpire(null);
    setTokenRefresh(null);
    setIsAdmin(false);
    setIsLoading(false);
  }, [dispatch]);

  const login = async (email: string, password: string) => {
    try {
      const res = await loginMutation.mutateAsync({ email, password });
      if (res?.code === 200 && res?.success) {
        const me = res.doctor;
        console.log('user', me);
        const status = me.status === 'INACTIVE';
        const meAdmin = (res as any)?.is_from_admin ?? false;

        handleAuth(
          res.token,
          me,
          true,
          status,
          String(res.token_expires),
          res.refresh_token,
          meAdmin
        );
      } else {
        throw new Error((res as any)?.message || 'Login failed');
      }
      return res;
    } catch (err: any) {
      toast({
        title: 'Login failed',
        message: err?.error_message || 'Email/Password is not correct.',
        variant: 'error'
      });
    }
  };

  const signup = async (email: string, password: string, first_name: string, last_name: string) => {
    try {
      const res = await signupMutation.mutateAsync({
        email,
        password,
        firstName: first_name,
        lastName: last_name
      });

      if (res?.code === 200 && res?.success) {
        const me = res.doctor;
        const status = me.status === 'INACTIVE';
        const meAdmin = (res as any)?.is_from_admin ?? false;

        handleAuth(
          res.token,
          me,
          true,
          status,
          String(res.token_expires),
          res.refresh_token,
          meAdmin
        );
      } else {
        throw new Error((res as any)?.message || 'Signup failed');
      }
      return res;
    } catch (err: any) {
      toast({
        title: 'Sign up failed',
        message: err?.message || 'Please try again.',
        variant: 'error'
      });
    }
  };

  const getMe = useCallback(async () => {
    const tok = storage.getItem('token');
    if (!tok) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);

    inflight.current?.abort();
    const ac = new AbortController();
    inflight.current = ac;

    try {
      const res = await doctorMe();
      const data = (res as any)?.data ?? res;

      if (data?.code === 200 && data?.success) {
        const me: IUser = data.doctor;
        const status = me.status === 'INACTIVE';
        const meAdmin = data?.is_from_admin ?? false;

        handleAuth(
          tok,
          me,
          true,
          status,
          storage.getItem('expiry'),
          storage.getItem('token-refresh'),
          meAdmin
        );
      } else {
        throw new Error(data?.message || 'Session expired');
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        logout();
      }
    } finally {
      if (!ac.signal.aborted) setIsLoading(false);
    }
  }, [handleAuth, logout, doctorMe]);

  const logMe = useCallback(() => {
    const t = storage.getItem('token');
    const e = storage.getItem('expiry');
    const r = storage.getItem('token-refresh');

    if (t) setToken(t);
    if (e) setExpire(e);
    if (r) setTokenRefresh(r);

    // No snapshot loading - rely on getMe() to fetch from API
    getMe();
  }, [getMe]);

  useEffect(() => {
    logMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        signup,
        getMe,
        logMe,
        user,
        isLoggedIn,
        isOnBoarding,
        isLoading,
        token,
        isAdmin
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
